import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import type { RevenuePoint } from "@/lib/admin-api";

// ---------------------------------------------------------------------------
// FCFA tooltip formatter
// ---------------------------------------------------------------------------

function formatFcfa(value: number): string {
  return `${new Intl.NumberFormat("fr-SN").format(value)} FCFA`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RevenueChartProps {
  readonly data: ReadonlyArray<RevenuePoint>;
  readonly isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Skeleton placeholder
// ---------------------------------------------------------------------------

function ChartSkeleton() {
  return (
    <div className="flex h-72 items-center justify-center">
      <div className="h-full w-full animate-pulse rounded-lg bg-stone-100" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RevenueChart({ data, isLoading }: RevenueChartProps) {
  const { t } = useTranslation(["admin", "dashboard"]);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-display text-lg font-semibold text-stone-900">
          {t("dashboard.charts.revenue_title")}
        </h3>
        <ChartSkeleton />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-display text-lg font-semibold text-stone-900">
          {t("dashboard.charts.revenue_title")}
        </h3>
        <div className="flex h-72 items-center justify-center text-sm text-stone-400">
          {t("dashboard.charts.no_data")}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 font-display text-lg font-semibold text-stone-900">
        {t("dashboard.charts.revenue_title")}
      </h3>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={[...data]} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: "#78716c" }}
              tickLine={false}
              axisLine={{ stroke: "#d6d3d1" }}
            />
            <YAxis
              tickFormatter={(v: number) => `${Math.round(v / 1000)}k`}
              tick={{ fontSize: 12, fill: "#78716c" }}
              tickLine={false}
              axisLine={false}
              width={48}
            />
            <Tooltip
              formatter={(value: number) => [formatFcfa(value), t("dashboard.charts.revenue_label")]}
              labelStyle={{ color: "#44403c", fontWeight: 600 }}
              contentStyle={{
                borderRadius: 8,
                border: "1px solid #e7e5e4",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#d97706"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: "#d97706", strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
